// ============================================
// KPI Stat Cards
// Row of 5 financial metrics following VendorDashboard pattern
// ============================================

import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Target,
  Stethoscope,
} from 'lucide-react';

interface KpiCardsProps {
  totalRevenueCents: number;
  orderCount: number;
  avgOrderValueCents: number;
  conversionRate: number;
  providerRevenueCents: number;
  loading: boolean;
}

interface CardDef {
  label: string;
  value: string;
  icon: typeof DollarSign;
  colorClass: string;
  bgClass: string;
}

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export function KpiCards({
  totalRevenueCents,
  orderCount,
  avgOrderValueCents,
  conversionRate,
  providerRevenueCents,
  loading,
}: KpiCardsProps) {
  const cards: CardDef[] = [
    {
      label: 'Revenue',
      value: formatCurrency(totalRevenueCents),
      icon: DollarSign,
      colorClass: 'text-gold',
      bgClass: 'bg-gold/10',
    },
    {
      label: 'Orders',
      value: orderCount.toLocaleString(),
      icon: ShoppingCart,
      colorClass: 'text-accent-light',
      bgClass: 'bg-accent-light/10',
    },
    {
      label: 'Avg Order Value',
      value: formatCurrency(avgOrderValueCents),
      icon: TrendingUp,
      colorClass: 'text-optimal',
      bgClass: 'bg-optimal/10',
    },
    {
      label: 'Conversion',
      value: formatPercent(conversionRate),
      icon: Target,
      colorClass: 'text-suboptimal',
      bgClass: 'bg-suboptimal/10',
    },
    {
      label: 'Provider Revenue',
      value: formatCurrency(providerRevenueCents),
      icon: Stethoscope,
      colorClass: 'text-purple-500',
      bgClass: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card, i) => (
        <div
          key={card.label}
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
          style={{ animationDelay: `${i * 0.08}s` }}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-text-secondary text-base font-medium">
              {card.label}
            </span>
            <div
              className={`w-10 h-10 rounded-lg ${card.bgClass} flex items-center justify-center`}
            >
              <card.icon size={20} className={card.colorClass} />
            </div>
          </div>
          <p className={`text-3xl font-bold ${card.colorClass}`}>
            {loading ? '--' : card.value}
          </p>
        </div>
      ))}
    </div>
  );
}
